import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileSpreadsheet, Sparkles, AlertCircle, Upload, Database } from 'lucide-react';

interface ImportCapTableStepProps {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  projectId?: string;
}

export function ImportCapTableStep({ data, onUpdate, onNext, projectId }: ImportCapTableStepProps) {
  const { user, signOut } = useAuth();
  const getToken = async () => {
    // In Supabase auth, we don't need a separate token - the session handles it
    return null;
  };
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'seed' | 'import' | 'skip'>('seed');
  const [error, setError] = useState<string | null>(null);

  const handleSeedData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get the auth token
      const token = await getToken();
      
      // Call worker endpoint to seed sample data
      const response = await fetch('/api/onboarding/seed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          projectId,
          organizationId: data.organizationId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to seed sample data');
      }

      // Track analytics
      await supabase
        .from('onboarding_analytics')
        .insert({
          user_id: user!.id,
          event_name: 'sample_data_seeded',
          event_data: { project_id: projectId }
        });

      onUpdate({ dataOption: 'seed' });
      onNext();
    } catch (err: any) {
      console.error('Error seeding data:', err);
      setError(err.message || 'Failed to seed sample data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = () => {
    // For now, just skip to next step
    // In a real implementation, this would open a file picker
    onUpdate({ dataOption: 'import' });
    onNext();
  };

  const handleSkip = () => {
    onUpdate({ dataOption: 'skip' });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Database className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold">Set up your cap table</h3>
          <p className="text-sm text-muted-foreground">
            Start with sample data to explore features, or import your existing cap table
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <Card 
          className={`cursor-pointer transition-colors ${
            selectedOption === 'seed' ? 'border-primary' : ''
          }`}
          onClick={() => setSelectedOption('seed')}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Use Sample Data (Recommended)
            </CardTitle>
            <CardDescription>
              Perfect for exploring Share Lattice features with realistic demo data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Pre-populated cap table with founders and investors</li>
              <li>• Sample vesting schedules and equity grants</li>
              <li>• Example funding rounds and valuations</li>
              <li>• Can be easily cleared later</li>
            </ul>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-colors ${
            selectedOption === 'import' ? 'border-primary' : ''
          }`}
          onClick={() => setSelectedOption('import')}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Import Existing Cap Table
            </CardTitle>
            <CardDescription>
              Upload your cap table from Excel, CSV, or other formats
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Support for Excel, CSV, and Carta exports</li>
              <li>• Automatic field mapping</li>
              <li>• Data validation and error checking</li>
              <li>• Preview before importing</li>
            </ul>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-colors ${
            selectedOption === 'skip' ? 'border-primary' : ''
          }`}
          onClick={() => setSelectedOption('skip')}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Start from Scratch
            </CardTitle>
            <CardDescription>
              Begin with an empty cap table and add data manually
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      <div className="pt-4">
        {selectedOption === 'seed' && (
          <Button 
            onClick={handleSeedData} 
            className="w-full" 
            disabled={isLoading}
          >
            {isLoading ? 'Creating Sample Data...' : 'Create Sample Data'}
          </Button>
        )}
        {selectedOption === 'import' && (
          <Button 
            onClick={handleImport} 
            className="w-full" 
            disabled={isLoading}
          >
            Continue to Import
          </Button>
        )}
        {selectedOption === 'skip' && (
          <Button 
            onClick={handleSkip} 
            className="w-full"
          >
            Start with Empty Cap Table
          </Button>
        )}
      </div>
    </div>
  );
}